import {
    app,
    Component,
    ojs,
    component,
    payload,
    v,
    state,
} from "modular-openscriptjs";
import { empty, clearSearchSuggestion, isMobile, setSearchQuery } from "../../../helpers";
import { api, dc } from "../../../declarations";
import SearchResultProductView from "../../Views/SearchResultProductView";
import Spinner from "../../Blocks/Spinner";
import QuickOrderCard from "../Search/QuickOrderCard";
import InfiniteScroll from "../../Blocks/InfiniteScroll";
import { dom } from "../../../fotastart";

const h = app("h");
const router = app("router");
const broker = app("broker");

ojs(SearchResultProductView, InfiniteScroll, Spinner, QuickOrderCard);

export default class BusinessProfileSearchResult extends Component {
    businessId = null;
    companyState = state(null);
    loading = state(true);

    pageMap = {
        products: { next: 1, total: 0 },
    };

    // See SearchResults.js for why this exists: only the most recently
    // dispatched request's response is allowed to render.
    _searchSeq = 0;

    constructor() {
        super();
        this.resetMap();
    }

    resetMap() {
        this.pageMap.products = { next: 1, total: 0 };

        // Also invalidate any request still in flight at the moment of the
        // reset, even before a replacement request goes out - so a slow
        // response from a business we've since navigated away from can
        // never land in the wrong business's list afterward.
        this._searchSeq++;
    }

    $$ojs = {
        routeChanged: () => this.resetMap(),
    };

    $$app = {
        has: {
            searchQuery: (ed, event) => {
                this.resetMap();
                const holder = dom.id("c-business-profile-search-results");
                if (holder) {
                    dom.empty(holder);
                }
                const marker = dom.get(
                    "[hook='business-profile-search-marker']",
                );
                this.getProducts("c-business-profile-search-results", marker);
            },
        },
    };

    async init() {
        this.loading.value = true;
        let params = app("router").params || {};
        this.businessId = params.businessId;

        try {
            let res = await api.get(`/companies/${this.businessId}`);
            if (res && res.data && res.data.company) {
                this.companyState.value = res.data.company;
            }
        } catch (error) {
            console.error(error);
        } finally {
            this.loading.value = false;
        }
    }

    render(...args) {
        let params = app("router").params || {};
        if (this.businessId !== params.businessId) {
            this.businessId = params.businessId;
            this.init();
        }

        return h.div(
            { class: "bg-light pb-5 py-md-5", style: "min-height: 100vh;" },
            v(this.loading, (loadingState) => {
                if (loadingState.value) {
                    return h.div(
                        {
                            class: "d-flex justify-content-center align-items-center w-100",
                            style: "height: 60vh;",
                        },
                        h.div({
                            class: "spinner-border text-primary",
                            role: "status",
                        }),
                    );
                }

                return v(this.companyState, (compState) => {
                    let company = compState.value;
                    if (!company) {
                        return h.div(
                            { class: "container px-4 py-5 text-center" },
                            h.h3(
                                { class: "fw-bold text-dark" },
                                "Business Not Found",
                            ),
                        );
                    }

                    return h.div(
                        // Header
                        h.div(
                            {
                                class: "bg-white border-bottom py-4 mb-5 shadow-sm sticky-top",
                                style: "z-index: 1020; top: 56px;",
                            },
                            h.div(
                                {
                                    class: "container px-4 d-flex align-items-center gap-3",
                                },
                                h.span(
                                    {
                                        class: "btn btn-light rounded-circle d-flex align-items-center justify-content-center",
                                        style: "width: 40px; height: 40px; cursor: pointer;",
                                        listeners: {
                                            click: () => {
                                                setSearchQuery("");
                                            },
                                        },
                                    },
                                    h.i({ class: "bi bi-arrow-left fs-5" }),
                                ),
                                h.div(
                                    h.h4(
                                        {
                                            class: "fw-bold mb-1 text-dark m-0",
                                            style: "font-size: 1.25rem;",
                                        },
                                        "Search Results ",
                                        v(dc.searchQuery, (q) =>
                                            !empty(q.value)
                                                ? "for " + q.value
                                                : "",
                                        ),
                                    ),
                                    h.div(
                                        {
                                            class: "d-flex align-items-center gap-2 mt-1",
                                        },
                                        company.logo_path
                                            ? h.img({
                                                  src: company.logo_path,
                                                  class: "rounded-circle flex-shrink-0",
                                                  style: "width: 24px; height: 24px; object-fit: cover;",
                                              })
                                            : h.div(
                                                  {
                                                      class: "rounded-circle flex-shrink-0 bg-primary text-white d-flex align-items-center justify-content-center fw-bold",
                                                      style: "width: 24px; height: 24px; font-size: 0.75rem;",
                                                  },
                                                  company.name
                                                      ? company.name
                                                            .charAt(0)
                                                            .toUpperCase()
                                                      : "?",
                                              ),
                                        h.span(
                                            {
                                                class: "text-dark fw-semibold small",
                                            },
                                            company.name,
                                        ),
                                        h.span(
                                            { class: "text-muted small" },
                                            "@",
                                            company.handle,
                                        ),
                                    ),
                                ),
                            ),
                        ),

                        // Products Infinite Scroll
                        h.div(
                            { class: "container" },
                            h.InfiniteScroll({
                                child: h.div({
                                    id: "c-business-profile-search-results",
                                    class: "d-flex gap-3 gap-md-4 flex-wrap justify-content-around justify-content-md-start",
                                }),
                                callback: (entries, observer, marker) => {
                                    entries.forEach((entry) => {
                                        if (!entry.isIntersecting) return;
                                        entry.target.setAttribute(
                                            "hook",
                                            "business-profile-search-marker",
                                        );
                                        this.populateProducts(
                                            "c-business-profile-search-results",
                                            marker,
                                        );
                                    });
                                },
                            }),
                        ),
                    );
                });
            }),
            ...args,
        );
    }

    $_mounted(componentId) {
        let c = component(componentId);
        if (!c) return;
        c.getProducts("c-business-profile-search-results");
    }

    async getProducts(holderId, spinnerHolder = null) {
        const requestSeq = ++this._searchSeq;

        let page = this.pageMap.products.next;
        let query = dc.searchQuery.value ?? "";

        let spinner = null;
        if (spinnerHolder) {
            spinner = h.Spinner({ parent: spinnerHolder, resetParent: true });
        }

        if (empty(query)) {
            spinner?.remove();
            return;
        }

        try {
            let res = await api.get(
                `/companies/${this.businessId}/products/search/query`,
                {
                    query,
                    page,
                    interval: 10,
                },
                { silent: true },
            );

            // Stale response - a newer request has since been dispatched.
            if (requestSeq !== this._searchSeq) {
                return;
            }

            if (res) {
                let products = res.data.products.data || [];
                let currentPage = res.data.products.current_page;
                this.pageMap.products.total = res.data.products.total ?? 0;

                const holder = dom.id(holderId);
                if (holder && currentPage === 1) {
                    dom.empty(holder);
                }

                for (let product of products) {
                    if (
                        holder &&
                        !dom.get(`[feed-product-id="p-${product.id}"]`, holder)
                    ) {
                        h.SearchResultProductView(product, {
                            class: "flex-shrink-0 h-100",
                            style: isMobile()
                                ? "width: 150px;"
                                : "width: 280px;",
                            parent: holder,
                            $feed_product_id: `p-${product.id}`,
                            listeners: {
                                click: () => {
                                    broker.send(
                                        app("$e").product.needs.display,
                                        payload({ product }),
                                    );
                                },
                            },
                        });
                    }
                }

                if (products.length === 0 && currentPage === 1) {
                    this._showEmpty(
                        holderId,
                        "No results found matching your search.",
                    );
                }

                let lastPageNum = res.data.products.last_page;
                if (currentPage >= lastPageNum || products.length === 0) {
                    if (
                        holder &&
                        !dom.get("[hook='quick-order-card']", holder)
                    ) {
                        let name = this.companyState.value?.handle
                            ? this.companyState.value?.name +
                              ` (@${this.companyState.value?.handle})`
                            : this.companyState.value?.name ||
                              "[Business Name]";

                        h.div(h.QuickOrderCard(`Bought from ${name}: `), {
                            parent: holder,
                            class: "w-100 mt-4",
                            $hook: "quick-order-card",
                        });
                    }
                    this.pageMap.products.next = currentPage;
                } else {
                    this.pageMap.products.next = currentPage + 1;
                }
            }
        } catch (e) {
            console.error(e);
        } finally {
            spinner?.remove();
            clearSearchSuggestion();
        }
    }

    async populateProducts(holderId, marker) {
        this.getProducts(holderId, marker);
    }

    _showEmpty(holderId, message) {
        const holder = dom.id(holderId);
        if (!holder) return;

        h.div(
            {
                parent: holder,
                resetParent: true,
                class: "card border-0 rounded-4 w-100 p-5 text-center bg-white shadow-sm border border-light",
            },
            h.div(
                {
                    class: "text-muted d-flex flex-column align-items-center justify-content-center gap-3",
                },
                h.i({
                    class: "bi bi-search fs-1 text-secondary opacity-50",
                }),
                h.h5({ class: "fw-bold text-dark mb-0" }, message),
            ),
        );
    }
}

ojs(BusinessProfileSearchResult);
